import { ThemeEvents } from '@theme/events';

const schedules = new Map();

export function loadPreviewSchedule(url, now, fetcher = fetch) {
  const cached = schedules.get(url);
  if (cached && now - cached.at < 60000) return cached.promise;
  const latest = new URL(url, typeof document === 'undefined' ? 'https://artesta.es' : document.baseURI);
  // Shopify only cache-busts allowlisted parameters. Use a minute version for
  // this dynamic projection; arbitrary query keys can keep serving old rates.
  latest.searchParams.set('v', String(Math.floor(now / 60000)));
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 8000);
  const promise = fetcher(latest.href, { credentials: 'omit', signal: controller.signal }).then(response => {
    if (!response.ok) throw new Error('Preview unavailable');
    return response.json();
  }).finally(() => clearTimeout(timer));
  schedules.set(url, { at: now, promise });
  return promise;
}

export function selectPreviewWindow(schedule, marketId, format, now) {
  if (schedule?.schema_version !== 1 || schedule.provisional !== true) return null;
  const market = schedule.markets?.[String(marketId).split('/').pop()];
  const row = market?.formats?.[format]?.find(value => value[0] <= now && now < value[1]);
  if (!row || row.length !== 9) return null;
  return row;
}

const setProducts = new WeakMap();

export function readSetDeliveryFormats(root) {
  const formats = new Set();
  for (const item of root.querySelectorAll('[data-set-item]')) {
    if (item.classList.contains('is-excluded')) continue;
    const source = item.querySelector('[data-product-json]');
    if (!source) return null;
    try {
      if (!setProducts.has(source)) setProducts.set(source, JSON.parse(source.textContent));
      const data = setProducts.get(source);
      const id = item.getAttribute('data-variant-id') ?? item.dataset.defaultVariantId;
      const variant = data.variants.find(value => String(value.id) === String(id));
      const prefix = variant?.sku?.slice(0, 1).toUpperCase();
      if (!variant?.available || !['P', 'C'].includes(prefix)) return null;
      formats.add(prefix === 'P' ? 'poster' : 'canvas');
    } catch { return null; }
  }
  return [...formats];
}

export function selectSetPreviewWindow(schedule, marketId, formats, now) {
  if (!Array.isArray(formats) || !formats.length) return null;
  const rows = formats.map(format => selectPreviewWindow(schedule, marketId, format, now));
  if (rows.some(row => !row) || rows.some(row => row[2] !== rows[0][2])) return null;
  try {
    for (const row of rows) {
      for (const [first, last] of [[2, 2], [3, 4], [5, 6], [7, 8]]) {
        formatPreviewDate(row[first], row[last], 'en');
      }
    }
    // The whole set is ready when its slowest included format is ready.
    return rows[0].map((value, index) => index < 3 ? value
      : rows.map(row => row[index]).sort().at(-1));
  } catch { return null; }
}

export function formatPreviewDate(first, last, locale) {
  const date = value => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(value || '')) throw new Error('Invalid preview date');
    const parsed = new Date(value + 'T12:00:00Z');
    if (!Number.isFinite(parsed.getTime()) || parsed.toISOString().slice(0, 10) !== value) throw new Error('Invalid preview date');
    return parsed;
  };
  const start = date(first), end = date(last);
  if (start > end) throw new Error('Invalid preview range');
  const formatter = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', timeZone: 'UTC',
    ...(first.slice(0, 4) !== last.slice(0, 4) ? { year: 'numeric' } : {}) });
  if (first === last) return formatter.format(start);
  return formatter.formatRange(start, end);
}

export function renderPreviewDates(host, schedule, now, setFormats = undefined) {
  const list = host.querySelector('ol');
  const title = host.querySelector('[data-preview-title]');
  const note = host.querySelector('[data-preview-note]');
  const transit = host.querySelector('[data-preview-transit]');
  const dates = [...host.querySelectorAll('[data-preview-date]')];
  list.hidden = true;
  title.textContent = host.dataset.fallback;
  if (note) note.textContent = host.dataset.note;
  if (transit) transit.removeAttribute('title');
  dates.forEach(element => { element.textContent = ''; });
  try {
    const row = setFormats === undefined
      ? selectPreviewWindow(schedule, host.dataset.market, host.dataset.format, now)
      : selectSetPreviewWindow(schedule, host.dataset.market, setFormats, now);
    if (!row || ![3, 4].includes(dates.length)) return false;
    const values = dates.length === 3 ? [[row[2], row[2]], [row[3], row[4]], [row[7], row[8]]]
      : [[row[2], row[2]], [row[3], row[4]], [row[5], row[6]], [row[7], row[8]]];
    const formatted = values.map(([first, last]) => formatPreviewDate(first, last, host.dataset.locale));
    if (host.dataset.today) {
      const zone = schedule.markets[String(host.dataset.market).split('/').pop()].timezone;
      if (zone) {
        const parts = new Intl.DateTimeFormat('en', { timeZone: zone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date(now));
        const part = type => parts.find(value => value.type === type)?.value;
        if (row[2] === `${part('year')}-${part('month')}-${part('day')}`) formatted[0] += ` · ${host.dataset.today}`;
      }
    }
    dates.forEach((element, index) => { element.textContent = formatted[index]; });
    title.textContent = host.dataset.title;
    if (transit) transit.title = `${transit.textContent.trim()}: ${formatPreviewDate(row[5], row[6], host.dataset.locale)}`;
    list.hidden = false;
    return true;
  } catch { return false; }
}

export function renderPreviewSummary(host, schedule, now) {
  host.textContent = host.dataset.fallback;
  try {
    const formats = host.dataset.surface === 'header' || host.dataset.format === 'all'
      ? ['poster', 'canvas'] : [host.dataset.format];
    if (host.dataset.mode === 'lead') {
      if (schedule?.schema_version !== 1 || schedule.provisional !== true) return false;
      const market = schedule.markets?.[String(host.dataset.market).split('/').pop()];
      const leads = formats.map(format => market?.lead_days?.[format]);
      if (leads.some(lead => !Array.isArray(lead) || lead.length !== 2
        || lead.some(value => !Number.isInteger(value) || value < 0) || lead[0] > lead[1])) return false;
      const first = Math.min(...leads.map(lead => lead[0]));
      const last = Math.max(...leads.map(lead => lead[1]));
      if (host.dataset.emphasis === 'true') {
        const label = host.ownerDocument.createElement('span'); label.textContent = `${host.dataset.title} `;
        const value = host.ownerDocument.createElement('strong'); value.className = 'artesta-shipping-information__lead-value';
        value.textContent = `${first}-${last} ${host.dataset.unit}`;
        host.replaceChildren(label, value);
      } else host.textContent = `${host.dataset.title} ${first}-${last} ${host.dataset.unit}`;
      return true;
    }
    const rows = formats.map(format => selectPreviewWindow(schedule, host.dataset.market, format, now));
    if (rows.some(row => !row)) return false;
    const first = rows.map(row => row[7]).sort()[0];
    const last = rows.map(row => row[8]).sort().at(-1);
    host.textContent = `${host.dataset.title} ${formatPreviewDate(first, last, host.dataset.locale)}`;
    return true;
  } catch { return false; }
}

let shippingViewSequence = 0;

function resetShippingFacts(host) {
  host.textContent = host.dataset.fallback;
  host.shippingRenderKey = null;
  return false;
}

export function renderShippingFacts(host, schedule, now = Date.now()) {
  try {
    if (schedule?.schema_version !== 1 || schedule.provisional !== true) return resetShippingFacts(host);
    const generated = Date.parse(schedule.generated_at);
    if (!Number.isFinite(generated) || generated > now + 300000 || now - generated > 86400000) return resetShippingFacts(host);
    const zones = schedule.shipping?.[host.dataset.country];
    if (!Array.isArray(zones) || !zones.length) return resetShippingFacts(host);
    for (const zone of zones) {
      if (!Array.isArray(zone.rates) || !zone.rates.length || zone.rates.some(rate => rate.currency !== host.dataset.currency
        || rate.amount === null || [rate.amount, rate.min, rate.max].some(value => value !== null
          && (!['string', 'number'].includes(typeof value) || String(value).trim() === '' || !Number.isFinite(Number(value)) || Number(value) < 0))
        || (rate.min !== null && rate.max !== null && Number(rate.min) > Number(rate.max)))) return resetShippingFacts(host);
    }
    const renderKey = JSON.stringify([zones, host.dataset.country, host.dataset.currency, host.dataset.locale,
      host.dataset.title, host.dataset.order, host.dataset.cost, host.dataset.from, host.dataset.to, host.dataset.free, host.dataset.any]);
    // Preserve destination selection and keyboard focus during periodic refreshes.
    if (host.shippingRenderKey === renderKey && host.querySelector('table')) return true;
    const money = new Intl.NumberFormat(host.dataset.locale, { style: 'currency', currency: host.dataset.currency });
    const doc = host.ownerDocument, fragment = doc.createDocumentFragment();
    const ordered = [...zones].sort((a, b) => (b.provinces?.length || 0) - (a.provinces?.length || 0));
    const zoneKeys = ordered.map(zone => JSON.stringify([zone.label, zone.provinces || []]));
    const previous = zoneKeys.indexOf(host.dataset.selectedZone);
    let selected = previous >= 0 ? previous : 0;
    const tabs = [], panels = [], prefix = `artesta-shipping-zone-${++shippingViewSequence}`;
    const nav = doc.createElement('div'); nav.className = 'artesta-shipping-information__destinations';
    nav.setAttribute('role', 'tablist'); nav.setAttribute('aria-label', host.dataset.title || host.dataset.cost);
    if (ordered.length > 1) fragment.append(nav);
    for (const [index, zone] of ordered.entries()) {
      const section = doc.createElement('div');
      if (ordered.length > 1) {
        const button = doc.createElement('button'); button.type = 'button'; button.textContent = zone.label;
        button.className = 'artesta-shipping-information__destination'; button.id = `${prefix}-tab-${index}`;
        button.setAttribute('role', 'tab'); button.setAttribute('aria-controls', `${prefix}-panel-${index}`);
        section.id = `${prefix}-panel-${index}`; section.setAttribute('role', 'tabpanel');
        section.setAttribute('aria-labelledby', button.id); section.tabIndex = 0;
        nav.append(button); tabs.push(button);
      }
      panels.push(section);
      const table = host.ownerDocument.createElement('table');
      table.className = 'artesta-shipping-information__table';
      table.setAttribute('aria-label', `${host.dataset.title || host.dataset.cost} · ${zone.label}`);
      const thead = table.createTHead();
      const heading = thead.insertRow();
      for (const text of [host.dataset.order, host.dataset.cost]) {
        const cell = host.ownerDocument.createElement('th'); cell.scope = 'col'; cell.textContent = text; heading.append(cell);
      }
      const body = table.createTBody();
      for (const rate of zone.rates) {
        const row = body.insertRow();
        const bounds = [];
        if (rate.min !== null && Number(rate.min) > 0) bounds.push(`${host.dataset.from} ${money.format(Number(rate.min))}`);
        if (rate.max !== null) bounds.push(`${host.dataset.to} ${money.format(Number(rate.max))}`);
        row.insertCell().textContent = bounds.join(' · ') || host.dataset.any || host.dataset.order;
        row.insertCell().textContent = Number(rate.amount) === 0 ? host.dataset.free : money.format(Number(rate.amount));
        if (Number(rate.amount) === 0) row.setAttribute('data-free', '');
      }
      section.append(table); fragment.append(section);
    }
    const select = (index, focus = false) => {
      selected = index; host.dataset.selectedZone = zoneKeys[index];
      panels.forEach((panel, i) => { panel.hidden = i !== index; });
      tabs.forEach((tab, i) => { tab.setAttribute('aria-selected', String(i === index)); tab.tabIndex = i === index ? 0 : -1; });
      if (focus) tabs[index].focus();
    };
    tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => select(index));
      tab.addEventListener('keydown', event => {
        const target = { ArrowRight: (selected + 1) % tabs.length, ArrowLeft: (selected + tabs.length - 1) % tabs.length,
          Home: 0, End: tabs.length - 1 }[event.key];
        if (target === undefined) return;
        event.preventDefault(); select(target, true);
      });
    });
    select(selected);
    host.replaceChildren(fragment); host.shippingRenderKey = renderKey;
    return true;
  } catch { return resetShippingFacts(host); }
}

if (typeof window !== 'undefined' && !customElements.get('artesta-delivery-preview')) {
  class DeliveryPreview extends HTMLElement {
    connectedCallback() {
      this.setRoot = this.localName === 'artesta-delivery-preview' ? this.closest('[data-collection-sets]') : null;
      this.scope = this.dataset.surface === 'header' ? document
        : this.closest('[id*="ProductInformation-"], .product-information, .shopify-section');
      this.onVariant = event => {
        const sku = event.detail?.resource?.sku;
        if (typeof sku !== 'string') return;
        this.dataset.format = sku.startsWith('C') ? 'canvas' : sku.startsWith('P') ? 'poster' : '';
        this.update();
      };
      this.followsVariant = !this.setRoot && (this.localName === 'artesta-delivery-preview'
        || (this.localName === 'artesta-delivery-summary' && this.dataset.mode === 'dates'
          && this.dataset.surface !== 'header'));
      if (this.followsVariant) this.scope?.addEventListener(ThemeEvents.variantUpdate, this.onVariant);
      if (this.setRoot) {
        this.setObserver = new MutationObserver(() => this.update());
        this.setObserver.observe(this.setRoot, { subtree: true, attributes: true, attributeFilter: ['data-variant-id', 'class'] });
      }
      this.update();
      this.refresh();
      this.interval = setInterval(() => { this.update(); this.refresh(); }, 30000);
    }
    refresh() {
      if (this.loading) return;
      this.loading = true;
      loadPreviewSchedule(this.dataset.schedule, Date.now()).then(schedule => {
        this.schedule = schedule;
        if (this.isConnected) this.update();
      }).catch(() => {
        this.schedule = undefined;
        if (this.isConnected) this.update();
      }).finally(() => { this.loading = false; });
    }
    update() {
      const renderer = this.localName === 'artesta-shipping-facts' ? renderShippingFacts
        : this.localName === 'artesta-delivery-summary' ? renderPreviewSummary : renderPreviewDates;
      renderer(this, this.schedule, Date.now(), this.setRoot ? readSetDeliveryFormats(this.setRoot) : undefined);
    }
    disconnectedCallback() {
      clearInterval(this.interval);
      this.setObserver?.disconnect();
      this.scope?.removeEventListener(ThemeEvents.variantUpdate, this.onVariant);
    }
  }
  customElements.define('artesta-delivery-preview', DeliveryPreview);
  customElements.define('artesta-delivery-summary', class extends DeliveryPreview {});
  customElements.define('artesta-shipping-facts', class extends DeliveryPreview {});
  customElements.define('artesta-shipping-page-remainder', class extends HTMLElement {
    connectedCallback() {
      const source = this.querySelector('template');
      if (!source) return;
      const blocks = [...source.content.querySelectorAll('div')].filter(block => block.querySelector('h3'));
      // The authorized page has seven questions; never reuse old delivery promises
      // if its structure changes and the targeted replacement cannot be verified.
      if (blocks.length !== 7) return;
      for (const [index, block] of blocks.entries()) {
        if (index < 2) continue;
        const details = document.createElement('details'); details.className = 'faq-item';
        const summary = document.createElement('summary'); summary.className = 'faq-item__summary';
        summary.textContent = block.querySelector('h3').textContent;
        const body = document.createElement('div'); body.className = 'faq-item__content';
        if (index === 4) {
          const text = document.createElement('p'); text.textContent = this.dataset.tracking; body.append(text);
        } else {
          for (const child of block.children) {
            if (child.tagName !== 'H3') body.append(child.cloneNode(true));
          }
        }
        body.querySelectorAll('script,style,iframe,form').forEach(node => node.remove());
        body.querySelectorAll('*').forEach(node => {
          for (const attr of [...node.attributes]) {
            if (attr.name.startsWith('on') || (['href', 'src'].includes(attr.name) && /^\s*javascript:/i.test(attr.value))) node.removeAttribute(attr.name);
          }
        });
        details.append(summary, body); this.append(details);
      }
      source.remove();
    }
  });
}
